import React, { useState, useRef, useMemo, useEffect } from "react";
import { Box, Button, Typography, FormControlLabel, Switch, Alert } from "@mui/material";
import { DataTable } from "../../components/DataTable";
import SupportForm from "./SupportForm";
import { AddCircleOutline, Search, Description, Business, PhoneInTalk } from "@mui/icons-material";
import { useAuth } from "../login/AuthContext";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import config from "../../config/config";
import { toast } from "react-toastify";
import { getCallerIdConfig, isNativePlatform } from "../../utils/callerIdIntegration";
import IconActionsRenderer from "../../components/IconActionsRenderer";
import { dateFormatter } from "../../components/DataTable/dateFormatter";
import { SupportStatusCellRenderer } from "../../components/DataTable/SupportStatusCellRenderer";

const SupportList = () => {
  const { token, userData } = useAuth();
  const navigate = useNavigate();
  const isAnaBayi = userData?.BayiKodu === 1;
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingSupportId, setEditingSupportId] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [showNonPozitifOfis, setShowNonPozitifOfis] = useState(false); // Pasif: Pozitif Ofis, Aktif: Diğer müşteriler
  const [callerIdTestResult, setCallerIdTestResult] = useState(null); // "ok" | "error" | null
  const dataTableRef = useRef(null);

  const callerIdConfig = getCallerIdConfig();
  const isMobile = isNativePlatform();
  const showCallerIdBox = true; // Hem web hem mobilde göster (mobilde test butonu aktif)


  // Saat formatını düzenle (HH:mm:ss -> HH:mm)
  const timeFormatter = (params) => {
    if (!params.value) return "";
    const timeStr = params.value.toString();
    // HH:mm:ss formatından HH:mm formatına çevir
    if (timeStr.includes(":")) {
      return timeStr.substring(0, 5); // İlk 5 karakter (HH:mm)
    }
    return timeStr;
  };

  // Pozitif Ofis müşterileri için column defs
  const pozitifOfisColumnDefs = [
    {
      headerName: "İşlemler",
      field: "actions",
      width: 130,
      minWidth: 130,
      maxWidth: 130,
      sortable: false,
      filter: false,
      cellRenderer: IconActionsRenderer,
      pinned: "left",
      cellClass: "action-cell-center",
    },
    {
      headerName: "Oluşturma Tarihi",
      field: "OlusturmaTarihi",
      valueFormatter: dateFormatter,
      sortable: true,
      filter: "agDateColumnFilter",
      width: 130,
    },
    {
      headerName: "Müşteri",
      field: "Musteri.Unvan",
      sortable: true,
      filter: true,
      width: 200,
      valueGetter: (params) => params.data?.Musteri?.Unvan || "",
    },
    {
      headerName: "Görüşülen Kişi",
      field: "GorusulenKisi",
      sortable: true,
      filter: true,
      width: 150,
    },
    {
      headerName: "Telefon",
      field: "Telefon",
      sortable: true,
      filter: true,
      width: 130,
    },
    {
      headerName: "Destek Türü",
      field: "DestekTuru",
      sortable: true,
      filter: true,
      width: 150,
    },
    {
      headerName: "Modül",
      field: "Modul",
      sortable: true,
      filter: true,
      width: 150,
    },
    {
      headerName: "Başlangıç Saati",
      field: "BaslangicSaati",
      sortable: true,
      filter: true,
      width: 150,
      valueFormatter: timeFormatter,
    },
    {
      headerName: "Bitiş Saati",
      field: "BitisSaati",
      sortable: true,
      filter: true,
      width: 150,
      valueFormatter: timeFormatter,
    },
    {
      headerName: "Durum",
      field: "Durum",
      sortable: true,
      filter: true,
      width: 150,
      cellRenderer: SupportStatusCellRenderer,
    },
    {
      headerName: "Kullanıcı",
      field: "Kullanici.KullaniciAdi",
      sortable: true,
      filter: true,
      width: 150,
      valueGetter: (params) => {
        const kullanici = params.data?.Kullanici;
        if (kullanici?.Adi && kullanici?.Soyadi) {
          return `${kullanici.Adi} ${kullanici.Soyadi}`;
        }
        return kullanici?.KullaniciAdi || "";
      },
    },
    // Oluşturan Bayi sütunu sadece ana bayi (BayiId 1) görsün
    ...(isAnaBayi
      ? [
          {
            headerName: "Oluşturan Bayi",
            field: "OlusturanBayi.BayiAdi",
            sortable: true,
            filter: true,
            width: 150,
            valueGetter: (params) => params.data?.OlusturanBayi?.BayiAdi || "—",
          },
        ]
      : []),
  ];

  // Pozitif Ofis dışı müşteriler için column defs (İşlemler sütunu yok)
  const nonPozitifOfisColumnDefs = [
    {
      headerName: "Oluşturma Tarihi",
      field: "OlusturmaTarihi",
      valueFormatter: (params) => {
        if (!params.value) return "";
        return params.value;
      },
      sortable: true,
      filter: true,
      width: 130,
    },
    {
      headerName: "Müşteri",
      field: "MusteriAdi",
      sortable: true,
      filter: true,
      width: 200,
    },
    {
      headerName: "Görüşülen Kişi",
      field: "GorusulenKisi",
      sortable: true,
      filter: true,
      width: 150,
    },
    {
      headerName: "Telefon",
      field: "Telefon",
      sortable: true,
      filter: true,
      width: 130,
    },
    {
      headerName: "Destek Türü",
      field: "DestekTuru",
      sortable: true,
      filter: true,
      width: 150,
    },
    {
      headerName: "Modül",
      field: "Modul",
      sortable: true,
      filter: true,
      width: 150,
    },
    {
      headerName: "Başlangıç Saati",
      field: "BaslangicSaati",
      sortable: true,
      filter: true,
      width: 150,
      valueFormatter: timeFormatter,
    },
    {
      headerName: "Bitiş Saati",
      field: "BitisSaati",
      sortable: true,
      filter: true,
      width: 150,
      valueFormatter: timeFormatter,
    },
    {
      headerName: "Durum",
      field: "Durum",
      sortable: true,
      filter: true,
      width: 150,
      cellRenderer: SupportStatusCellRenderer,
    },
    {
      headerName: "Kullanıcı",
      field: "AdSoyad",
      sortable: true,
      filter: true,
      width: 150,
      valueGetter: (params) => {
        return params.data?.AdSoyad || params.data?.KullaniciAdi || "";
      },
    },
    // Oluşturan Bayi sütunu sadece ana bayi (BayiId 1) görsün
    ...(isAnaBayi
      ? [
          {
            headerName: "Oluşturan Bayi",
            field: "OlusturanBayiAdi",
            sortable: true,
            filter: true,
            width: 150,
            valueGetter: (params) => params.data?.OlusturanBayiAdi || "—",
          },
        ]
      : []),
  ];

  // Toggle durumuna göre column defs seç
  const supportColumnDefs = useMemo(() => {
    return showNonPozitifOfis ? nonPozitifOfisColumnDefs : pozitifOfisColumnDefs;
  }, [showNonPozitifOfis, isAnaBayi]);

  // Toggle durumuna göre endpoint seç
  const apiEndpoint = useMemo(() => {
    return showNonPozitifOfis ? "/destek/non-pozitif-ofis" : "/destek";
  }, [showNonPozitifOfis]);

  // Arama veya toggle değişince tabloyu yenile (müşteri listesi gibi)
  useEffect(() => {
    if (dataTableRef.current?.refreshData) dataTableRef.current.refreshData();
  }, [searchText, showNonPozitifOfis]);

  const handleEditSupport = (supportId) => {
    setEditingSupportId(supportId);
    setIsDrawerOpen(true);
  };

  const handleDeleteSupport = async (supportId) => {
    try {
      const api = axios.create({
        baseURL: config.baseURL,
        headers: { Authorization: `Bearer ${token}` },
      });

      const response = await api.delete(`/destek/${supportId}`);

      if (response.status < 200 || response.status >= 300) {
        throw new Error(response.data?.message || "Silme işlemi başarısız");
      }

      toast.success("Destek kaydı başarıyla silindi");
      if (dataTableRef.current?.refreshData) {
        dataTableRef.current.refreshData();
      }
    } catch (error) {
      console.error("Destek silme hatası:", error);
      toast.error(error.message || "Destek kaydı silinirken hata oluştu");
      throw error;
    }
  };

  const handleClose = () => {
    setIsDrawerOpen(false);
    setEditingSupportId(null);
    if (dataTableRef.current?.refreshData) {
      dataTableRef.current.refreshData();
    }
  };

  const openDrawerForAdd = () => {
    setEditingSupportId(null);
    setIsDrawerOpen(true);
  };

  return (
    <Box sx={{ p: 3 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
          paddingRight: "56px",
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            color: '#333',
            fontSize: '1.5rem',
            margin: 0
          }}
        >
          Destek Kayıtları
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Description />}
          onClick={() => navigate("/destek/rapor")}
          size="medium"
          sx={{
            bgcolor: "#00A7B5",
            "&:hover": { bgcolor: "#008B98" },
          }}
        >
          DESTEK RAPORU
        </Button>
      </div>

      {/* Caller ID — mobilde arama kaydı; bağlantı testi buradan yapılır */}
      {showCallerIdBox && (
        <Alert
          severity={callerIdConfig ? (callerIdTestResult === "ok" ? "success" : callerIdTestResult === "error" ? "warning" : "info") : "warning"}
          sx={{ mb: 2 }}
          action={
            callerIdConfig ? (
              <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                <Button
                  color="inherit"
                  size="small"
                  onClick={async () => {
                    setCallerIdTestResult(null);
                    try {
                      const base = (callerIdConfig.baseUrl || "").replace(/\/+$/, "");
                      await axios.get(`${base}/health`, { timeout: 8000 });
                      setCallerIdTestResult("ok");
                      toast.success("Caller ID sunucusuna ulaşıldı");
                    } catch (e) {
                      setCallerIdTestResult("error");
                      toast.error("Sunucuya ulaşılamadı. Aynı Wi‑Fi ve sunucu adresini kontrol edin.");
                    }
                  }}
                >
                  Bağlantıyı test et
                </Button>
                <Button
                  color="inherit"
                  size="small"
                  variant="outlined"
                  onClick={async () => {
                    // Telefonda da çalışsın: uygulamanın kullandığı API adresini kullan
                    const apiBase = (config.baseURL || "").replace(/\/+$/, "");
                    const url = `${apiBase}/caller-id/incoming-call`;
                    const devicePhone = callerIdConfig.devicePhone || userData?.Tel || "";
                    const headers = {
                      "Content-Type": "application/json",
                      ...(callerIdConfig.apiKey ? { "x-caller-id-api-key": callerIdConfig.apiKey } : {}),
                    };
                    if (token) headers.Authorization = `Bearer ${token}`;
                    try {
                      const res = await axios.post(
                        url,
                        {
                          phone_number: "05550000000",
                          device_phone: devicePhone,
                          status: "ringing",
                        },
                        { headers, timeout: 15000 }
                      );
                      const created = res?.data?.destek?.created;
                      toast.success(created ? "Test kaydı oluşturuldu." : "İstek gitti, kayıt oluşmadı (cihaz numarası kontrolü).");
                      if (dataTableRef.current?.refreshData) dataTableRef.current.refreshData();
                    } catch (e) {
                      const msg = e.response?.data?.message || e.message || "İstek atılamadı.";
                      const status = e.response?.status;
                      const code = e.code;
                      let detail = e.response?.data?.detail || "";
                      if (status === 401) detail = "API anahtarı: sunucu .env'de CALLER_ID_API_KEY yorum satırında olsun.";
                      if (code === "ERR_NETWORK" || code === "ECONNABORTED") detail = "Aynı Wi‑Fi, sunucu açık mı?";
                      toast.error(detail ? `${msg} — ${detail}` : msg);
                    }
                  }}
                >
                  Test: sahte arama gönder
                </Button>
              </Box>
            ) : null
          }
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
            <PhoneInTalk fontSize="small" /> Caller ID (gelen arama → destek kaydı)
          </Box>
          {!callerIdConfig ? (
            isMobile
              ? "Giriş yaptıktan sonra bu sayfayı açın; ayarlar kaydedilir. Cihaz numarası (Kullanici.Tel) veritabanında dolu olmalı."
              : "Caller ID sadece mobil uygulamada aktiftir. Mobilde giriş yapınca ayarlar kaydedilir; arama geldiğinde otomatik destek kaydı oluşur."
          ) : (
            <>
              Sunucu: {callerIdConfig.baseUrl || "—"} · Cihaz: {callerIdConfig.devicePhone || "(boş)"}
              {callerIdTestResult === "ok" && " · Bağlantı OK"}
              {callerIdTestResult === "error" && " · Sunucuya ulaşılamadı"}
              {isMobile && (
                <Box component="span" display="block" sx={{ mt: 1, fontSize: "0.85rem", opacity: 0.95 }}>
                  <strong>Gerçek arama kayıt oluşturmuyorsa:</strong> Ayarlar → Uygulamalar → Lisans PO → <strong>İzinler</strong> (Telefon, Arama kayıtları, Rehber: Açık) → <strong>Pil</strong> (Kısıtlama yok / Sınırsız). Uygulamayı son uygulamalardan kapatmayın (kaydırarak kapatma). Xiaomi/Huawei vb.: "Otomatik başlat" veya "Arka planda çalıştır" açık olsun.
                </Box>
              )}
            </>
          )}
        </Alert>
      )}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "15px",
          marginBottom: "20px",
          padding: "10px 0",
        }}
      >
        <FormControlLabel
          control={
            <Switch
              checked={showNonPozitifOfis}
              onChange={(e) => {
                const newValue = e.target.checked;
                setShowNonPozitifOfis(newValue);
                // Toggle değiştiğinde tabloyu yenile
                setTimeout(() => {
                  if (dataTableRef.current?.refreshData) {
                    dataTableRef.current.refreshData();
                  }
                  // Cache'i temizle
                  if (dataTableRef.current?.purgeInfiniteCache) {
                    dataTableRef.current.purgeInfiniteCache();
                  }
                }, 200);
              }}
              color="primary"
            />
          }
          label={
            <Box display="flex" alignItems="center" gap={1}>
              <Business fontSize="small" />
              <Typography variant="body2" fontWeight={500}>
                {showNonPozitifOfis ? "Diğer Müşteriler" : "Pozitif Ofis Müşterileri"}
              </Typography>
            </Box>
          }
          sx={{ mr: 2 }}
        />
        <input
          type="text"
          placeholder="Cari / müşteri adı veya diğer alanlarda ara (en az 3 karakter)"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{
            padding: "10px 15px",
            width: "250px",
            border: "1px solid #ddd",
            borderRadius: "4px",
            fontSize: "14px",
          }}
          onFocus={(e) => {
            e.target.style.outline = "none";
            e.target.style.borderColor = "#00A7B5";
            e.target.style.boxShadow = "0 0 0 2px rgba(23, 162, 184, 0.1)";
          }}
          onBlur={(e) => {
            e.target.style.borderColor = "#ddd";
            e.target.style.boxShadow = "none";
          }}
        />
        {!showNonPozitifOfis && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddCircleOutline />}
            onClick={openDrawerForAdd}
            size="medium"
            sx={{
              bgcolor: "#00A7B5",
              "&:hover": { bgcolor: "#008B98" },
              ml: 2,
            }}
          >
            Ekle
          </Button>
        )}
      </div>

      <DataTable
        ref={dataTableRef}
        tableType="custom"
        columnDefs={supportColumnDefs}
        apiEndpoint={apiEndpoint}
        searchText={searchText}
        pageSize={14}
        onEditAction={showNonPozitifOfis ? undefined : handleEditSupport}
        onRowDoubleClicked={showNonPozitifOfis ? undefined : (e) => e.data?.Id != null && handleEditSupport(e.data.Id)}
        onDeleteAction={showNonPozitifOfis ? undefined : handleDeleteSupport}
        deleteConfirmMessage="Bu destek kaydını silmek istediğinizden emin misiniz?"
        idField="Id"
        customContext={showNonPozitifOfis ? { showEdit: false, showDelete: false } : undefined}
      />

      <SupportForm
        open={isDrawerOpen}
        onClose={handleClose}
        supportId={editingSupportId}
      />
    </Box>
  );
};

export default SupportList;
